import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

/**
 * Renders a sanitized HTML string from RichTextEditor.
 *
 * Strips scripts, event handlers, and disallowed attributes before injecting
 * via dangerouslySetInnerHTML. Falls back to plain text rendering for
 * legacy rows that pre-date the RTE migration (they'll have no tags).
 *
 * Props:
 *   html   — string of HTML (or plain text)
 *   as     — element tag, default 'div'
 *   style  — inline style passed to the wrapper
 *   className — extra class on the wrapper
 */
export default function SafeHtml({ html = '', as: Tag = 'div', style, className = '' }) {
  const clean = useMemo(() => {
    if (!html) return '';
    return DOMPurify.sanitize(String(html), {
      // No scripts, no event handlers, no embeds. Allow common formatting tags + links.
      ALLOWED_TAGS: [
        'p', 'br', 'span', 'div', 'strong', 'em', 'u', 's',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'blockquote', 'pre', 'code',
        'a', 'img',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'style'],
      // Force external links to open in new tab + no-opener for safety.
      ADD_ATTR: ['target', 'rel'],
    });
  }, [html]);

  return (
    <Tag
      className={`safe-html ${className}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
